import { createContext, useContext, useReducer, useMemo } from "react";
import PropTypes from "prop-types";
import Cookie from "js-cookie";
import { transformMenuData } from "utils/transformMenuData";
import { transformModuleMenuData } from "utils/transformModuleMenuData";
import { AUTH_STORAGE_KEY } from "constants/auth";
const MaterialUI = createContext();
MaterialUI.displayName = "MaterialUIContext";

function reducer(state, action) {
  switch (action.type) {
    case "SET_USER": {
      return { ...state, user: action.value };
    }
    case "MINI_SIDEBAR": {
      return { ...state, miniSidebar: action.value };
    }
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }
    case "WHITE_SIDENAV": {
      return { ...state, whiteSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value };
    }
    case "DIRECTION": {
      return { ...state, direction: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    case "DARKMODE": {
      return { ...state, darkMode: action.value };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// Rebuilds the logged-in user (including the route/menu tree and expanded
// sidebar state) from data persisted at login or on module selection, so a
// browser refresh doesn't lose the session and drop every route. Only
// trusted when a valid jwt_token cookie is present. The menu tree is always
// regenerated from raw data rather than deserialized, since its icon
// elements can't survive a JSON round-trip.
function getPersistedSession() {
  const token = Cookie.get("jwt_token");
  const stored = localStorage.getItem(AUTH_STORAGE_KEY);
  if (!token || !stored) return { user: null, miniSidebar: false };
  try {
    const parsed = JSON.parse(stored);
    const menu =
      parsed.menuSource === "module"
        ? transformModuleMenuData(parsed.menu)
        : transformMenuData(parsed.menu);
    return {
      user: { ...parsed, menu },
      miniSidebar: Boolean(parsed.miniSidebar),
    };
  } catch (error) {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return { user: null, miniSidebar: false };
  }
}

// Material Dashboard 2 React context provider
function MaterialUIControllerProvider({ children }) {
  const persistedSession = getPersistedSession();
  const initialState = {
    miniSidenav: false,
    miniSidebar: persistedSession.miniSidebar,
    transparentSidenav: false,
    whiteSidenav: false,
    sidenavColor: "info",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    darkMode: false,
    user: persistedSession.user,
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

// Material Dashboard 2 React custom hook for using context
function useMaterialUIController() {
  const context = useContext(MaterialUI);

  if (!context) {
    throw new Error(
      "useMaterialUIController should be used inside the MaterialUIControllerProvider."
    );
  }

  return context;
}

// Typechecking props for the MaterialUIControllerProvider
MaterialUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Context module functions
const setMiniSidebar = (dispatch, value) => dispatch({ type: "MINI_SIDEBAR", value });
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (dispatch, value) => dispatch({ type: "WHITE_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch, value) => dispatch({ type: "DARKMODE", value });
const setUser = (dispatch, value) => dispatch({ type: "SET_USER", value });

export {
  MaterialUIControllerProvider,
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
  setUser,
  setMiniSidebar,
};
